import { isPlatformBrowser } from '@angular/common';
import { Component, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { PaymentsService } from '@services/payments/payments.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';
import { LottieComponent } from 'ngx-lottie';

type PaymentStatus = 'loading' | 'success' | 'cancelled' | 'error';

@Component({
	selector: 'app-payment-result',
	imports: [SharedModule, ButtonComponent, LottieComponent, SpinnerComponent],
	templateUrl: './payment-result.component.html',
	styleUrl: './payment-result.component.scss',
})
export class PaymentResultComponent implements OnInit {
	private platformId = inject(PLATFORM_ID);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private paymentsService = inject(PaymentsService);
	private transloco = inject(TranslocoService);

	isBrowser = isPlatformBrowser(this.platformId);
	status = signal<PaymentStatus>('loading');
	orderId = signal<string | null>(null);
	errorMessage = signal<string | null>(null);

	successOptions = {
		path: 'assets/layout/lottie/success.json',
		loop: false,
		autoplay: true,
	};

	errorOptions = {
		path: 'assets/layout/lottie/error.json',
		loop: false,
		autoplay: true,
	};

	ngOnInit(): void {
		if (!this.isBrowser) return;

		const payment = this.route.snapshot.queryParamMap.get('payment');
		const sessionId = this.route.snapshot.queryParamMap.get('session_id');
		const orderIdParam = this.route.snapshot.queryParamMap.get('order_id');
		const subscriptionIdParam = this.route.snapshot.queryParamMap.get('subscription_id');

		this.orderId.set(orderIdParam);

		if (payment === 'cancelled') {
			this.status.set('cancelled');
			return;
		}

		if (payment === 'success' && sessionId && orderIdParam) {
			this.processPaymentSuccess(orderIdParam, sessionId);
		} else if (payment === 'success' && subscriptionIdParam) {
			// Subscriptions are activated by the Stripe webhook: nothing to confirm here, go to the list
			this.router.navigate(['/my-subscriptions']);
		} else {
			this.status.set('error');
			this.errorMessage.set(this.transloco.translate('payment.result.invalidParams'));
		}
	}

	private processPaymentSuccess(orderId: string, sessionId: string): void {
		this.paymentsService.processPaymentSuccess(orderId, sessionId).subscribe({
			next: (response) => {
				if (response.success) {
					// Go straight to orders when the payment succeeded
					this.router.navigate(['/orders']);
				} else {
					this.status.set('error');
					this.errorMessage.set(response.message || this.transloco.translate('payment.result.processError'));
				}
			},
			error: (err) => {
				console.error('Error processing payment:', err);
				this.status.set('error');
				this.errorMessage.set(translateApiError(err, this.transloco, 'payment.result.processError'));
			},
		});
	}

	goToOrders(): void {
		this.router.navigate(['/orders']);
	}

	goToBoost(): void {
		this.router.navigate(['/boost']);
	}

	retryPayment(): void {
		this.router.navigate(['/boost']);
	}
}
